'use client';

/**
 * Auth Event Provider
 *
 * Shared React provider that all frontend apps can use to subscribe to auth events.
 * Centralizes session state management and provides callbacks for session modals.
 *
 * Usage:
 * ```tsx
 * import { authEventBus } from '@makanly/sdk';
 * import { AuthEventProvider, SessionModals } from '@makanly/ui-kit';
 *
 * <AuthEventProvider
 *   eventBus={authEventBus}
 *   onLogout={handleLogout}
 *   onKeepLoggedIn={refreshToken}
 * >
 *   {children}
 *   <SessionModals />
 * </AuthEventProvider>
 * ```
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { AuthEventBus, SessionState } from './auth-events';
import { AuthEventType } from './auth-events';

interface AuthEventContextType {
  /** Current session state: 'active' | 'warning' | 'expired' */
  sessionState: SessionState;
  /** When the session will expire (for warning state) */
  expiresAt: number | null;
  /** Dismiss the warning modal (user chose to ignore) */
  dismissWarning: () => void;
  /** Handle logout (clears state and calls onLogout callback) */
  handleLogout: () => void;
  /** Handle keep logged in (refreshes token) */
  handleKeepLoggedIn: () => Promise<void>;
}

const AuthEventContext = createContext<AuthEventContextType | undefined>(undefined);

export interface AuthEventProviderProps {
  children: React.ReactNode;
  /** The auth event bus instance from @makanly/sdk */
  eventBus: AuthEventBus;
  /** Callback when user should be logged out */
  onLogout: () => void;
  /** Callback to refresh the session, returns true if successful */
  onKeepLoggedIn: () => Promise<boolean>;
  /** Optional storage key to clear on logout (default: 'klcubepos_user') */
  storageKey?: string;
}

export function AuthEventProvider({
  children,
  eventBus,
  onLogout,
  onKeepLoggedIn,
  storageKey = 'klcubepos_user',
}: AuthEventProviderProps) {
  const [sessionState, setSessionState] = useState<SessionState>('active');
  const [expiresAt, setExpiresAt] = useState<number | null>(null);

  useEffect(() => {
    // Subscribe to session warning events
    const unsubWarning = eventBus.subscribe(AuthEventType.SESSION_WARNING, (event) => {
      console.log('[AuthEventProvider] Session warning received:', event);
      setSessionState('warning');
      setExpiresAt(event.expiresAt || null);
    });

    // Subscribe to session expired events
    const unsubExpired = eventBus.subscribe(AuthEventType.SESSION_EXPIRED, (event) => {
      console.log('[AuthEventProvider] Session expired received:', event);
      setSessionState('expired');
      setExpiresAt(null);
    });

    // Subscribe to session restored events
    const unsubRestored = eventBus.subscribe(AuthEventType.SESSION_RESTORED, () => {
      console.log('[AuthEventProvider] Session restored');
      setSessionState('active');
      setExpiresAt(null);
    });

    // Subscribe to logout requested events
    const unsubLogout = eventBus.subscribe(AuthEventType.LOGOUT_REQUESTED, () => {
      console.log('[AuthEventProvider] Logout requested');
      setSessionState('expired');
      setExpiresAt(null);
    });

    // Check initial state from event bus
    const initialState = eventBus.getSessionState();
    if (initialState !== 'active') {
      setSessionState(initialState);
    }

    return () => {
      unsubWarning();
      unsubExpired();
      unsubRestored();
      unsubLogout();
    };
  }, [eventBus]);

  const dismissWarning = useCallback(() => {
    setSessionState('active');
    setExpiresAt(null);
  }, []);

  const handleLogoutClick = useCallback(() => {
    // Reset event bus state
    eventBus.resetSessionState();

    // Clear local state
    setSessionState('active');
    setExpiresAt(null);

    // Clear storage
    if (typeof window !== 'undefined' && storageKey) {
      try {
        localStorage.removeItem(storageKey);
      } catch (e) {
        console.warn('[AuthEventProvider] Failed to clear storage:', e);
      }
    }

    // Call the provided logout callback
    onLogout();
  }, [eventBus, onLogout, storageKey]);

  const handleKeepLoggedInClick = useCallback(async () => {
    try {
      const success = await onKeepLoggedIn();
      if (success) {
        // Reset state on success
        setSessionState('active');
        setExpiresAt(null);
        eventBus.resetSessionState();
      }
    } catch (error) {
      console.error('[AuthEventProvider] Keep logged in failed:', error);
      // If refresh failed, session is expired
      setSessionState('expired');
    }
  }, [eventBus, onKeepLoggedIn]);

  const value: AuthEventContextType = {
    sessionState,
    expiresAt,
    dismissWarning,
    handleLogout: handleLogoutClick,
    handleKeepLoggedIn: handleKeepLoggedInClick,
  };

  return <AuthEventContext.Provider value={value}>{children}</AuthEventContext.Provider>;
}

/**
 * Hook to access auth event context
 * Must be used within AuthEventProvider
 */
export function useAuthEvents(): AuthEventContextType {
  const context = useContext(AuthEventContext);
  if (!context) {
    throw new Error('useAuthEvents must be used within AuthEventProvider');
  }
  return context;
}
